import { createClientGateway, enableGatewaySecond } from "api/client-gateways";
import { getAllGatewaysByClient } from "api/gateways";
import DivLoader from "components/divloader/DivLoader";
import { useContext, useEffect, useState } from "react";
import { DragDropContext, Draggable, DropResult, Droppable } from "react-beautiful-dnd";
import { toast } from "react-hot-toast";
import { GatewayTypes } from "./CreateClientGatewaysModal";
import { ClientContext } from "ClientProvider";
import { Link } from "react-router-dom";

const smart_routingimg = require("../../../../assets/svg/smart_routing.svg");

const reorder = (list: any[], start: number, end: number) => {
  const result = Array.from(list);
  const [removed] = result.splice(start, 1);
  result.splice(end, 0, removed);
  return result as any[];
};

const ArrangeClientModal = ({ id, value, fetchClients }: { id: string; value: any; fetchClients: any }) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [addedGateways, setAddedGateways] = useState<any[]>([]);
  const [newArr, setNewArr] = useState<any[]>([]);
  const [isSmartRoutingEnabled, setIsSmartRoutingEnabled] = useState<boolean>(false);
  const [algorithm, setAlgorithm] = useState<string>("1");
  const { singleRoleData } = useContext(ClientContext);

  useEffect(() => {
    setIsSmartRoutingEnabled(value?.isSmartRouteEnabled);
    setAlgorithm(String(value?.sequential_algorithm || "1"));
    fetchAddedGateways();
  }, [value, id]);

  const fetchAddedGateways = () => {
    setIsLoading(true);
    getAllGatewaysByClient(id)
      .then((data) => {
        setAddedGateways(
          data.map((ele: any) => ({ ...ele, auth_info: { ...ele.auth_info, priority: ele.auth_info?.priority ?? 0 } }))
            .sort((a: any, b: any) => a.auth_info?.priority - b.auth_info?.priority)
        );
      })
      .catch((err) => toast.error(err?.response?.data?.message ?? "Error fetching gateways"))
      .finally(() => setIsLoading(false));
  };

  const handleSmartRouting = () => {
    localStorage.setItem("clientIds", id);
    const inputData = {
      id: id,
      isSmartRouteEnabled: isSmartRoutingEnabled,
    };
    enableGatewaySecond(inputData).then(() => {}).catch(() => {});
  };

  const onDragEnd = (newItems: any[]) => {
    setAddedGateways(newItems);
    setNewArr(newItems.map((ele, i) => ({ ...ele, auth_info: { ...ele.auth_info, priority: i + 1 } })));
  };

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) { onDragEnd(addedGateways); return; }
    onDragEnd(reorder(addedGateways, result.source.index, result.destination.index));
  };

  const handleSave = () => {
    const gateway_priority = addedGateways.map((d, i) => ({ client_id: d?.client_id, gateway_id: d?.gateway_id, priority: i + 1 }));
    if (!isSmartRoutingEnabled) {
      const inputData = {
        id: id,
        isSmartRouteEnabled: isSmartRoutingEnabled,
        sequential_algorithm: parseInt(algorithm),
        gateway_priority,
      };
      enableGatewaySecond(inputData)
        .then(() => {}).catch(() => {});
    }
    newArr.forEach((input_data) => {
      const cleaned = { client_id: input_data.client_id, gateway_id: input_data.gateway_id, auth_info: input_data.auth_info };
      createClientGateway({ client_id: cleaned.client_id, gateway_id: cleaned.gateway_id, method: "put", body: { client_id: cleaned.client_id, gateway_id: cleaned.gateway_id, auth_info: cleaned.auth_info } })
        .then(() => {}).catch((err) => toast.error(err?.response?.data?.message ?? "Error saving"));
    });
    toast.success("Priority saved successfully");
    fetchClients();
  };

  return (
    <div className="acm-root">

      {/* Header */}
      <div className="acm-page-header">
        <div className="acm-header-icon">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 3h6l6 18h6"/><path d="M14.25 8.25H21"/>
          </svg>
        </div>
        <div>
          <h3 className="acm-title">{isSmartRoutingEnabled ? "Smart Routing" : "Gateway Routing"}</h3>
          <p className="acm-subtitle">{isSmartRoutingEnabled ? "AI-powered intelligent gateway selection" : "Set priority order for payment gateway routing"}</p>
        </div>
      </div>

      {/* Smart route toggle + algorithm */}
      <div className="acm-config-card">
        <div className="acm-config-row">
          <div className="acm-config-left">
            <div className="acm-config-icon">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/></svg>
            </div>
            <div>
              <p className="acm-config-title">Smart Routing</p>
              <p className="acm-config-sub">Automatically optimise gateway selection based on performance</p>
            </div>
          </div>
          <label className="acm-toggle">
            <input type="checkbox" className="acm-toggle-inp" checked={isSmartRoutingEnabled} onChange={() => setIsSmartRoutingEnabled(!isSmartRoutingEnabled)} />
            <div className="acm-toggle-track"><div className="acm-toggle-thumb" /></div>
            <span className={`acm-toggle-lbl${isSmartRoutingEnabled ? " acm-toggle-lbl--on" : ""}`}>{isSmartRoutingEnabled ? "On" : "Off"}</span>
          </label>
        </div>

        {!isSmartRoutingEnabled && (
          <div className="acm-algo-row">
            <p className="acm-algo-label">Routing Algorithm</p>
            <div className="acm-algo-options">
              {[{ v: "1", l: "Top to Bottom", desc: "Routes to the highest priority gateway first" }, { v: "3", l: "Round Robin", desc: "Distributes evenly across all gateways" }].map((opt) => (
                <label key={opt.v} className={`acm-algo-opt${algorithm === opt.v ? " acm-algo-opt--active" : ""}`}>
                  <input type="radio" name="algo" value={opt.v} checked={algorithm === opt.v} onChange={(e) => setAlgorithm(e.target.value)} className="acm-radio" />
                  <div>
                    <div className="acm-algo-name">{opt.l}</div>
                    <div className="acm-algo-desc">{opt.desc}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Content area */}
      {isLoading ? (
        <div className="acm-loading"><div className="acm-spinner" /><p className="acm-loading-txt">Loading gateways…</p></div>
      ) : isSmartRoutingEnabled ? (
        <div className="acm-smart-card">
          <img src={smart_routingimg.default} alt="Smart routing" className="acm-smart-img" loading="lazy" />
          <h3 className="acm-smart-title">Smart Routing Enabled</h3>
          <p className="acm-smart-desc">The system will intelligently route payments based on real-time performance metrics, success rates, and transaction history.</p>
          <Link to={`/admin/smart-routing/${id}`}>
            <button className="acm-smart-btn" onClick={handleSmartRouting}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/></svg>
              Configure Smart Routing
            </button>
          </Link>
        </div>
      ) : addedGateways.length > 0 ? (
        <>
          <div className="acm-drag-card">
            <div className="acm-drag-header">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="16 3 21 3 21 8"/><line x1="4" y1="20" x2="21" y2="3"/><polyline points="21 16 21 21 16 21"/><line x1="15" y1="15" x2="21" y2="21"/></svg>
              Drag to reorder gateway priority
            </div>
            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId="droppable">
                {(provided) => (
                  <div {...provided.droppableProps} ref={provided.innerRef} className="acm-drag-list">
                    {addedGateways.map((data, index) => {
                      const img_src = GatewayTypes.filter((e: any) => e.name === data.name)[0]?.image;
                      return (
                        <Draggable key={data?.gateway_id} draggableId={data?.gateway_id?.toString()} index={index}>
                          {(provided, snapshot) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              className={`acm-drag-item${snapshot.isDragging ? " acm-drag-item--dragging" : ""}`}
                              style={provided.draggableProps.style}
                            >
                              <div className="acm-drag-handle">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></svg>
                              </div>
                              <div className="acm-drag-priority">{index + 1}</div>
                              {singleRoleData[0]?.client?.value?.client_show_method_name ? (
                                <>
                                  {img_src && <img src={img_src} alt={data?.name} loading="lazy" className="acm-gateway-img" />}
                                  <span className="acm-gateway-name">{data?.name}</span>
                                </>
                              ) : (
                                <span className="acm-gateway-id">{data?.client_gateway_id}</span>
                              )}
                            </div>
                          )}
                        </Draggable>
                      );
                    })}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          </div>

          <div className="acm-save-row">
            <p className="acm-save-hint">Drag items to reorder, then save to apply the new priority</p>
            <button className="acm-save-btn" onClick={handleSave} disabled={!!isLoading}>
              {isLoading ? <span className="acm-spinner" /> : (
                <>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                  Save Priority
                </>
              )}
            </button>
          </div>
        </>
      ) : (
        <div className="acm-empty">
          <div className="acm-empty-icon">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>
          </div>
          <p className="acm-empty-title">No gateways configured</p>
          <p className="acm-empty-sub">Add at least two payment gateways to set routing priority</p>
        </div>
      )}

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');
        .acm-root { font-family: 'Plus Jakarta Sans', sans-serif; display: flex; flex-direction: column; gap: 20px; }
        .acm-page-header { display: flex; align-items: flex-start; gap: 14px; }
        .acm-header-icon { width: 44px; height: 44px; border-radius: 12px; background: linear-gradient(135deg,#f0fdfa,#ccfbf1); border: 1.5px solid #99f6e4; display: flex; align-items: center; justify-content: center; color: #0d9488; flex-shrink: 0; }
        .acm-title { font-size: 16px; font-weight: 700; color: #0f172a; margin-bottom: 3px;line-height:22px }
        .acm-subtitle { font-size: 13px; color: #64748b; font-weight: 500; }
        /* Config card */
        .acm-config-card { background: #ffffff; border: 1px solid #e2e8f0; border-radius: 16px; padding: 20px; display: flex; flex-direction: column; gap: 20px; }
        .acm-config-row { display: flex; align-items: center; justify-content: space-between; gap: 14px; flex-wrap: wrap; }
        .acm-config-left { display: flex; align-items: center; gap: 12px; }
        .acm-config-icon { width: 38px; height: 38px; border-radius: 10px; background: linear-gradient(135deg,#eff6ff,#dbeafe); border: 1.5px solid #bfdbfe; display: flex; align-items: center; justify-content: center; color: #2563eb; flex-shrink: 0; }
        .acm-config-title { font-size: 14px; font-weight: 700; color: #0f172a; margin-bottom: 2px; }
        .acm-config-sub { font-size: 12px; color: #64748b; font-weight: 500; max-width: 350px;margin-top: 0px; }
        /* Toggle */
        .acm-toggle { display: flex; align-items: center; gap: 8px; cursor: pointer; }
        .acm-toggle-inp { display: none; }
        .acm-toggle-track { width: 44px; height: 24px; background: #e2e8f0; border-radius: 100px; position: relative; transition: background .2s; flex-shrink: 0; }
        .acm-toggle-inp:checked ~ .acm-toggle-track { background: #2563eb; }
        .acm-toggle-thumb { position: absolute; top: 3px; left: 3px; width: 18px; height: 18px; border-radius: 50%; background: #fff; transition: transform .2s; box-shadow: 0 1px 4px rgba(0,0,0,.18); }
        .acm-toggle-inp:checked ~ .acm-toggle-track .acm-toggle-thumb { transform: translateX(20px); }
        .acm-toggle-lbl { font-size: 13px; font-weight: 600; color: #64748b; }
        .acm-toggle-lbl--on { color: #2563eb; }
        /* Algorithm */
        .acm-algo-row { border-top: 1px solid #f1f5f9; padding-top: 16px; }
        .acm-algo-label { font-size: 12.5px; font-weight: 700; color: #374151; text-transform: uppercase; letter-spacing: .4px; margin-bottom: 12px; }
        .acm-algo-options { display: flex; gap: 12px; flex-wrap: wrap; }
        .acm-algo-opt { display: flex; align-items: flex-start; gap: 10px; padding: 14px 16px; border: 1.5px solid #e2e8f0; border-radius: 12px; cursor: pointer; transition: all .15s; flex: 1; min-width: 160px; }
        .acm-algo-opt--active { border-color: #2563eb; background: #eff6ff; }
        .acm-radio { accent-color: #2563eb; margin-top: 2px; flex-shrink: 0; }
        .acm-algo-name { font-size: 13px; font-weight: 700; color: #0f172a; margin-bottom: 2px; }
        .acm-algo-desc { font-size: 12px; color: #64748b; font-weight: 500; }
        /* Smart routing */
        .acm-smart-card { background: #ffffff; border: 1px solid #e2e8f0; border-radius: 16px; padding: 40px 24px; display: flex; flex-direction: column; align-items: center; gap: 16px; text-align: center; }
        .acm-smart-img { width: 220px; opacity: .85; }
        .acm-smart-title { font-size: 18px; font-weight: 800; color: #0f172a; }
        .acm-smart-desc { font-size: 13.5px; color: #64748b; line-height: 1.6; max-width: 380px; }
        .acm-smart-btn { display: inline-flex; align-items: center; gap: 8px; font-family: 'Plus Jakarta Sans', sans-serif; font-size: 14px; font-weight: 700; color: #fff; padding: 12px 24px; border-radius: 11px; border: none; background: linear-gradient(135deg,#1d4ed8,#2563eb); cursor: pointer; box-shadow: 0 4px 14px rgba(37,99,235,.3); transition: all .15s; text-decoration: none; }
        .acm-smart-btn:hover { transform: translateY(-1px); box-shadow: 0 8px 24px rgba(37,99,235,.38); }
        /* Drag */
        .acm-drag-card { background: #ffffff; border: 1px solid #e2e8f0; border-radius: 16px; overflow: hidden; }
        .acm-drag-header { display: flex; align-items: center; gap: 7px; padding: 14px 18px; background: #fafbfc; border-bottom: 1px solid #f1f5f9; font-size: 12.5px; font-weight: 700; color: #64748b; text-transform: uppercase; letter-spacing: .4px; }
        .acm-drag-list { padding: 12px; display: flex; flex-direction: column; gap: 8px; }
        .acm-drag-item { display: flex; align-items: center; gap: 12px; background: #fff; border: 1.5px solid #e2e8f0; border-radius: 12px; padding: 14px 16px; cursor: grab; transition: box-shadow .15s, border-color .15s; user-select: none; }
        .acm-drag-item:hover { border-color: #93c5fd; box-shadow: 0 2px 12px rgba(37,99,235,.1); }
        .acm-drag-item--dragging { background: #eff6ff; border-color: #93c5fd; box-shadow: 0 8px 24px rgba(37,99,235,.2); cursor: grabbing; }
        .acm-drag-handle { color: #94a3b8; display: flex; align-items: center; flex-shrink: 0; }
        .acm-drag-priority { width: 26px; height: 26px; border-radius: 7px; background: linear-gradient(135deg,#eff6ff,#dbeafe); border: 1.5px solid #bfdbfe; display: flex; align-items: center; justify-content: center; font-size: 12px; font-weight: 700; color: #2563eb; flex-shrink: 0; }
        .acm-gateway-img { height: 36px; width: 64px; object-fit: contain; }
        .acm-gateway-name { font-size: 14px; font-weight: 700; color: #0f172a; flex: 1; }
        .acm-gateway-id { font-size: 13px; font-weight: 600; color: #374151; font-family: 'Courier New', monospace; flex: 1; }
        /* Save row */
        .acm-save-row { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 12px; }
        .acm-save-hint { font-size: 12.5px; color: #94a3b8; font-weight: 500; }
        .acm-save-btn { display: inline-flex; align-items: center; gap: 7px; font-family: 'Plus Jakarta Sans', sans-serif; font-size: 13.5px; font-weight: 700; color: #fff; padding: 11px 22px; border-radius: 10px; border: none; background: linear-gradient(135deg,#1d4ed8,#2563eb); cursor: pointer; box-shadow: 0 3px 12px rgba(37,99,235,.3); transition: all .15s; }
        .acm-save-btn:hover { transform: translateY(-1px); }
        .acm-save-btn:disabled { opacity: .6; cursor: not-allowed; transform: none; }
        /* States */
        .acm-loading { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 48px; gap: 12px; }
        .acm-spinner { width: 26px; height: 26px; border: 3px solid #e2e8f0; border-top-color: #2563eb; border-radius: 50%; animation: acmSpin .7s linear infinite; }
        @keyframes acmSpin { to { transform: rotate(360deg); } }
        .acm-loading-txt { font-size: 13px; color: #94a3b8; font-weight: 500; }
        .acm-empty { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 56px 24px; gap: 12px; background: #fff; border: 1px solid #e2e8f0; border-radius: 16px; }
        .acm-empty-icon { width: 56px; height: 56px; border-radius: 14px; background: #f8fafc; border: 1.5px solid #e2e8f0; display: flex; align-items: center; justify-content: center; color: #cbd5e1; }
        .acm-empty-title { font-size: 14px; font-weight: 700; color: #64748b; }
        .acm-empty-sub { font-size: 13px; color: #94a3b8; font-weight: 500; text-align: center; max-width: 280px; }
      `}</style>
    </div>
  );
};

export default ArrangeClientModal;